package org.moxultimate.topwithmox.wallpapercrud.main

import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.last
import kotlinx.coroutines.launch
import kotlinx.coroutines.suspendCancellableCoroutine
import org.khronos.webgl.ArrayBuffer
import org.khronos.webgl.Int8Array
import org.moxultimate.topwithmox.core.base.ViewModel
import org.moxultimate.topwithmox.core.state.APIState
import org.moxultimate.topwithmox.utils.toTitleCase
import org.moxultimate.topwithmox.wallpaper.api.WallpaperRepository
import org.moxultimate.topwithmox.wallpaper.data.WallpaperRequest
import org.moxultimate.topwithmox.wallpaper.data.WallpaperResponse
import org.moxultimate.topwithmox.wallpapercrud.api.WallpaperCRUDRepository
import org.w3c.files.File
import org.w3c.files.FileReader
import kotlin.coroutines.resume
import kotlin.coroutines.resumeWithException

/**
 * WallpaperCRUDViewModel
 * Manages wallpaper list state (reads via [repository]) and all write operations
 * (upload, update, delete via [crudRepository]). Supports type/series chip filters,
 * live search, and cursor-based pagination.
 *
 * @param repository Read-only wallpaper repository (list, filter, series, tags).
 * @param crudRepository Write repository (upload, update, delete, saveSeries, saveTag).
 */
class WallpaperCRUDViewModel(
    private val repository: WallpaperRepository,
    private val crudRepository: WallpaperCRUDRepository,
) : ViewModel() {

    // ── Operation state (upload / update / delete) ────────────────────────────

    private val _uploadState = MutableStateFlow<WallpaperCRUDState>(WallpaperCRUDState.Idle)
    val uploadState: StateFlow<WallpaperCRUDState> = _uploadState

    // ── List state ────────────────────────────────────────────────────────────

    private val _listState = MutableStateFlow<WallpaperListState>(WallpaperListState.Loading)
    val listState: StateFlow<WallpaperListState> = _listState

    private val _isLoadingMore = MutableStateFlow(false)
    val isLoadingMore: StateFlow<Boolean> = _isLoadingMore

    private val _hasMoreInCurrentView = MutableStateFlow(true)
    val hasMoreInCurrentView: StateFlow<Boolean> = _hasMoreInCurrentView

    // ── General list (no active filters) ─────────────────────────────────────

    private val accumulatedWallpapers = mutableListOf<WallpaperResponse>()
    private var lastId: Long = Long.MAX_VALUE
    private var hasMore = true

    // ── Filter chips ──────────────────────────────────────────────────────────

    /** Selected type chips (Phone / Desktop / Widescreen / Vertical). */
    private val _selectedTypes = MutableStateFlow<Set<String>>(emptySet())
    val selectedTypes: StateFlow<Set<String>> = _selectedTypes

    /** Selected series chips. */
    private val _selectedSeries = MutableStateFlow<Set<String>>(emptySet())
    val selectedSeries: StateFlow<Set<String>> = _selectedSeries

    // ── Live search text (client-side filter on loaded wallpapers) ────────────

    private val _searchText = MutableStateFlow("")
    val searchText: StateFlow<String> = _searchText

    // ── Filtered results (populated when any chip is active) ──────────────────

    private val filteredWallpapers = mutableListOf<WallpaperResponse>()
    private var filterBeforeId = Long.MAX_VALUE
    private var filterHasMore = true
    private var isFilterLoading = false

    private val isAnyFilterActive: Boolean
        get() = _selectedTypes.value.isNotEmpty() || _selectedSeries.value.isNotEmpty()

    // ── Series / Tags lists ───────────────────────────────────────────────────

    private val _allSeries = MutableStateFlow<List<String>>(emptyList())
    val allSeries: StateFlow<List<String>> = _allSeries

    private val _allTags = MutableStateFlow<List<String>>(emptyList())
    val allTags: StateFlow<List<String>> = _allTags

    // ── Live search ───────────────────────────────────────────────────────────

    /**
     * Updates the live search text and immediately re-filters the currently loaded wallpapers.
     * No API call is made — this is a pure client-side filter.
     *
     * @param text Text to search for in wallpaper names and tags.
     */
    fun setSearchText(text: String) {
        _searchText.value = text
        _listState.value = buildListState()
    }

    // ── Clear all filters ─────────────────────────────────────────────────────

    /**
     * Resets all active chip filters and search text, restoring the general wallpaper list.
     */
    fun clearAllFilters() {
        _selectedTypes.value = emptySet()
        _selectedSeries.value = emptySet()
        _searchText.value = ""
        filteredWallpapers.clear()
        filterBeforeId = Long.MAX_VALUE
        filterHasMore = true
        isFilterLoading = false
        _hasMoreInCurrentView.value = hasMore
        _listState.value = buildListState()
    }

    // ── Type filter ───────────────────────────────────────────────────────────

    /**
     * Vertical and Phone share a portrait aspect ratio.
     * Selecting Vertical implicitly includes Phone in the API filter.
     */
    private fun effectiveTypes(selected: Set<String>): Set<String> =
        if ("Vertical" in selected) selected + "Phone" else selected

    /**
     * Toggles [type] in/out of the active type filter and re-fetches via the combined filter API.
     *
     * @param type One of: Phone, Desktop, Widescreen, Vertical.
     */
    fun toggleType(type: String) {
        val current = _selectedTypes.value
        _selectedTypes.value = if (type in current) current - type else current + type
        resetAndFetchFilter()
    }

    // ── Series filter ─────────────────────────────────────────────────────────

    /**
     * Toggles [series] in/out of the active series filter and re-fetches via the combined filter API.
     *
     * @param series Series name as stored in the tags field.
     */
    fun toggleSeries(series: String) {
        val current = _selectedSeries.value
        _selectedSeries.value = if (series in current) current - series else current + series
        resetAndFetchFilter()
    }

    // ── Filter fetch ──────────────────────────────────────────────────────────

    private fun resetAndFetchFilter() {
        filteredWallpapers.clear()
        filterBeforeId = Long.MAX_VALUE
        filterHasMore = true
        isFilterLoading = false
        if (isAnyFilterActive) {
            fetchFilterPage(isInitialLoad = true)
        } else {
            _hasMoreInCurrentView.value = hasMore
            _listState.value = buildListState()
        }
    }

    private fun fetchFilterPage(isInitialLoad: Boolean) {
        if (isFilterLoading && !isInitialLoad) return
        isFilterLoading = true
        viewModelScope.launch {
            _isLoadingMore.value = true
            val types = effectiveTypes(_selectedTypes.value).map { it.lowercase() }
            val series = _selectedSeries.value.toList()
            repository.getFilteredWallpapers(
                types = types,
                series = series,
                beforeId = filterBeforeId,
            ).collect { state ->
                when (state) {
                    is APIState.Loading -> if (isInitialLoad) _listState.value = WallpaperListState.Loading
                    is APIState.Error -> {
                        isFilterLoading = false
                        _isLoadingMore.value = false
                        _listState.value = WallpaperListState.Error(state.error, state.statusCode)
                    }
                    is APIState.Success -> {
                        isFilterLoading = false
                        _isLoadingMore.value = false
                        val page = state.data
                        filterHasMore = page.size >= 20
                        filteredWallpapers.addAll(page)
                        if (page.isNotEmpty()) filterBeforeId = page.last().id
                        _hasMoreInCurrentView.value = filterHasMore
                        _listState.value = buildListState()
                    }
                }
            }
        }
    }

    // ── Load more ─────────────────────────────────────────────────────────────

    /**
     * Called by the scroll handler when the user nears the bottom.
     * Loads the next filter page when filters are active, or the next general page otherwise.
     */
    fun loadMoreIfNeeded() {
        if (isAnyFilterActive) {
            if (filterHasMore && !isFilterLoading) fetchFilterPage(isInitialLoad = false)
        } else {
            if (hasMore && !_isLoadingMore.value) fetchWallpapers(isInitialLoad = false)
        }
    }

    // ── General list ──────────────────────────────────────────────────────────

    /** Resets and fetches the first page of wallpapers. Call from LaunchedEffect(Unit). */
    fun loadWallpapers() {
        accumulatedWallpapers.clear()
        lastId = Long.MAX_VALUE
        hasMore = true
        filteredWallpapers.clear()
        filterBeforeId = Long.MAX_VALUE
        filterHasMore = true
        isFilterLoading = false
        _selectedTypes.value = emptySet()
        _selectedSeries.value = emptySet()
        _searchText.value = ""
        _hasMoreInCurrentView.value = true
        fetchWallpapers(isInitialLoad = true)
    }

    private fun fetchWallpapers(isInitialLoad: Boolean) {
        viewModelScope.launch {
            _isLoadingMore.value = true
            repository.getAllWallpapers(lastId).collect { state ->
                _listState.value = when (state) {
                    is APIState.Loading -> {
                        if (isInitialLoad) WallpaperListState.Loading else _listState.value
                    }
                    is APIState.Error -> {
                        _isLoadingMore.value = false
                        WallpaperListState.Error(state.error, state.statusCode)
                    }
                    is APIState.Success -> {
                        val page = state.data
                        _isLoadingMore.value = false
                        if (page.size < 20) hasMore = false
                        accumulatedWallpapers.addAll(page)
                        if (page.isNotEmpty()) lastId = accumulatedWallpapers.last().id
                        _hasMoreInCurrentView.value = hasMore
                        buildListState()
                    }
                }
            }
        }
    }

    // ── Series / Tags loading ─────────────────────────────────────────────────

    /** Fetches all distinct series from the API. Call once on page load. */
    fun loadAllSeries() {
        viewModelScope.launch {
            repository.getAllSeries().collect { state ->
                if (state is APIState.Success) _allSeries.value = state.data.distinct().sorted()
            }
        }
    }

    /** Fetches all tags from the API. Call once on page load. */
    fun loadAllTags() {
        viewModelScope.launch {
            repository.getAllTags().collect { state ->
                if (state is APIState.Success) _allTags.value = state.data.distinct().sorted()
            }
        }
    }

    // ── State builder ─────────────────────────────────────────────────────────

    /**
     * Builds the list state from the correct source, then applies live search text client-side.
     */
    private fun buildListState(): WallpaperListState.Success {
        val source = if (isAnyFilterActive) filteredWallpapers.toList() else accumulatedWallpapers.toList()
        val text = _searchText.value.trim()
        val result = if (text.isBlank()) source
                     else source.filter { w ->
                         w.tags.contains(text, ignoreCase = true) ||
                         w.name.contains(text, ignoreCase = true)
                     }
        return WallpaperListState.Success(
            wallpapers = result,
            typeList = result.map { it.type }.distinct(),
            hasMore = if (isAnyFilterActive) filterHasMore else hasMore,
        )
    }

    // ── Upload (batch add) ────────────────────────────────────────────────────

    /**
     * Reads each entry's files as ByteArray then uploads them one-by-one to
     * POST /wallpapers. Stops immediately if any single upload fails.
     */
    fun uploadBatch(formEntries: List<WallpaperFormEntry>) {
        viewModelScope.launch {
            _uploadState.value = WallpaperCRUDState.Loading
            try {
                for ((index, entry) in formEntries.withIndex()) {
                    val request = WallpaperRequest(
                        title = entry.title,
                        description = entry.description,
                        type = entry.type,
                        series = entry.series,
                        tags = entry.tags,
                        resolution = entry.resolution,
                        videoLink = entry.videoLink,
                        imagePath = readFileAsBytes(entry.imageFile!!),
                        thumbnailPath = readFileAsBytes(entry.thumbnailFile!!),
                        panoramaPath = entry.panoramaFile?.let { readFileAsBytes(it) } ?: ByteArray(0),
                    )
                    when (val result = crudRepository.uploadSingleWallpaper(request)) {
                        is APIState.Error -> {
                            _uploadState.value = WallpaperCRUDState.Error(
                                message = result.error,
                                statusCode = result.statusCode,
                                failedEntryIndex = index,
                            )
                            return@launch
                        }
                        is APIState.Success -> {
                            // Register series and each tag so the lookup tables stay in sync
                            crudRepository.saveSeries(entry.series.trim().toTitleCase())
                            entry.tags
                                .split("|")
                                .map { it.trim().toTitleCase() }
                                .filter { it.isNotBlank() }
                                .distinct()
                                .forEach { crudRepository.saveTag(it) }
                        }
                        else -> Unit
                    }
                }
                _uploadState.value = WallpaperCRUDState.Success("Successfully uploaded!")
            } catch (e: Throwable) {
                _uploadState.value = WallpaperCRUDState.Error(e.message ?: "Failed to read files")
            }
        }
    }

    // ── Update (edit existing) ────────────────────────────────────────────────

    /** Updates a single wallpaper via PUT /wallpapers/{id}. */
    fun updateWallpaper(id: Long, entry: WallpaperFormEntry) {
        viewModelScope.launch {
            _uploadState.value = WallpaperCRUDState.Loading
            try {
                val imageBytes    = entry.imageFile?.let { readFileAsBytes(it) }
                val thumbBytes    = entry.thumbnailFile?.let { readFileAsBytes(it) }
                val panoramaBytes = entry.panoramaFile?.let { readFileAsBytes(it) }
                when (val result = crudRepository.updateWallpaper(
                    id             = id,
                    title          = entry.title.takeIf { it.isNotBlank() },
                    description    = entry.description.takeIf { it.isNotBlank() },
                    type           = entry.type.takeIf { it.isNotBlank() },
                    series         = entry.series.takeIf { it.isNotBlank() },
                    tags           = entry.tags.takeIf { it.isNotBlank() },
                    resolution     = entry.resolution.takeIf { it.isNotBlank() },
                    videoLink      = entry.videoLink.takeIf { it.isNotBlank() },
                    imageBytes     = imageBytes,
                    thumbnailBytes = thumbBytes,
                    panoramaBytes  = panoramaBytes,
                ).last()) {
                    is APIState.Error -> _uploadState.value = WallpaperCRUDState.Error(result.error)
                    else -> _uploadState.value = WallpaperCRUDState.Success("Successfully updated!")
                }
            } catch (e: Throwable) {
                _uploadState.value = WallpaperCRUDState.Error(e.message ?: "Update failed")
            }
        }
    }

    // ── Delete ────────────────────────────────────────────────────────────────

    /** Deletes a wallpaper by [id] via DELETE /wallpapers/{id}. */
    fun deleteWallpaper(id: Long) {
        viewModelScope.launch {
            _uploadState.value = WallpaperCRUDState.Loading
            try {
                when (val result = crudRepository.deleteWallpaperById(id).last()) {
                    is APIState.Error -> _uploadState.value = WallpaperCRUDState.Error(result.error)
                    else -> _uploadState.value = WallpaperCRUDState.Success("Successfully deleted!")
                }
            } catch (e: Throwable) {
                _uploadState.value = WallpaperCRUDState.Error(e.message ?: "Delete failed")
            }
        }
    }

    // ── Shared ────────────────────────────────────────────────────────────────

    fun resetState() {
        _uploadState.value = WallpaperCRUDState.Idle
    }

    /** Reads a browser File as a ByteArray using FileReader. */
    private suspend fun readFileAsBytes(file: File): ByteArray = suspendCancellableCoroutine { cont ->
        val reader = FileReader()
        reader.onload = {
            val buffer = (reader.result as ArrayBuffer)
            val bytes = Int8Array(buffer).unsafeCast<ByteArray>()
            cont.resume(bytes)
        }
        reader.onerror = {
            cont.resumeWithException(Exception("Failed to read file: ${file.name}"))
        }
        reader.readAsArrayBuffer(file)
    }
}

/**
 * UI-layer form state for a single wallpaper entry (add or edit).
 * [imageFile] and [thumbnailFile] are required; [panoramaFile] is optional.
 * Files are null when no new file has been selected.
 */
data class WallpaperFormEntry(
    val imageFile: File? = null,
    val thumbnailFile: File? = null,
    val panoramaFile: File? = null,
    val title: String = "",
    val description: String = "",
    val type: String = "",
    val series: String = "",
    val tags: String = "",
    val resolution: String = "",
    val videoLink: String = "",
) {
    /** True only when all required fields and both mandatory files are present (used for add flow). */
    val isValid: Boolean
        get() = imageFile != null && thumbnailFile != null &&
                title.isNotBlank() && type.isNotBlank() && series.isNotBlank()

    /** True when required text fields are filled (used for update flow — files are optional). */
    val isValidForUpdate: Boolean
        get() = title.isNotBlank() && type.isNotBlank() && series.isNotBlank()

    /** Names of required fields that are still empty or missing — used to show validation hints. */
    val missingFields: List<String>
        get() = buildList {
            if (imageFile == null) add("Image")
            if (thumbnailFile == null) add("Thumbnail")
            if (title.isBlank()) add("Title")
            if (type.isBlank()) add("Type")
            if (series.isBlank()) add("Series")
        }
}
